import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconName } from 'app/shared/igntr-icons';
import { SalesProfileQuestionnaireDto } from 'app/api/models/salesProfile/salesProfileQuestionnaireDto';
import {
  SALES_PROFILE_QUESTIONS,
  SALES_PROFILE_STEP_ICONS,
} from './sales-profile-content';

export interface SalesProfileFinishPayload {
  answersJson: string;
  freeText: string;
}

export interface SalesProfileProgressPayload {
  answersJson: string;
  freeText: string;
  currentIndex: number;
}

interface QuestionOption {
  /** Stable id (A–E) used only for local selection tracking. */
  id: string;
  label: string;
  /**
   * Methodology tag the answer maps to (track for Q1, focus area for Q2–Q5,
   * motivation for Q6). Always the Danish tag — it is the key the scoring and
   * the generated plan match on, so it is never translated.
   */
  tag: string;
}

interface Question {
  number: number;
  title: string;
  /** e.g. "Vælg 2" / "Vælg op til 2". */
  hint: string;
  /** Optional grey helper line under the hint (Q6). */
  note?: string;
  /** Minimum answers required to advance (2 for Q1–Q5, 0 for the optional Q6). */
  min: number;
  /** Maximum answers selectable. */
  max: number;
  options: QuestionOption[];
  hasFreeText?: boolean;
  freeTextLabel?: string;
  freeTextPlaceholder?: string;
}

interface Step {
  icon: IconName;
  title: string;
  sub: string;
}

@Component({
  selector: 'app-sales-profile-questionnaire',
  templateUrl: './sales-profile-questionnaire.component.html',
  styleUrls: ['./sales-profile-questionnaire.component.scss'],
  standalone: false,
})
export class SalesProfileQuestionnaireComponent implements OnChanges {
  readonly letters = ['A', 'B', 'C', 'D', 'E', 'F'];

  /** Set by the host while the finish POST is in flight — disables the finish
   *  button so answers can't be submitted twice. */
  @Input() submitting = false;

  /**
   * Saved partial progress to hydrate. A JSON string in the same shape
   * buildAnswers() produces ({ answers: [{ q, options, tags }] }). Applied once
   * by the host before render.
   */
  @Input() initialAnswers: string | null = null;
  /** Question index to resume at (clamped to the valid range). */
  @Input() initialIndex = 0;

  /**
   * Admin-managed wording for the user's content language, already resolved by
   * the server (their language, Danish as fallback). Only wording: it is merged
   * onto the fixed structure, so switching language never invalidates saved
   * answers.
   */
  @Input() content: SalesProfileQuestionnaireDto | null = null;

  @Output() finish = new EventEmitter<SalesProfileFinishPayload>();
  /** Emitted on every navigation so the host can persist partial progress. */
  @Output() progress = new EventEmitter<SalesProfileProgressPayload>();

  steps: Step[] = [];
  questions: Question[] = [];

  currentIndex = 0;
  /** Selected option ids per question, indexed by question position. */
  selections: string[][] = SALES_PROFILE_QUESTIONS.map(() => []);
  freeText = '';
  private hydrated = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content']) {
      this.steps = this.buildSteps();
      this.questions = this.buildQuestions();
    }
    if (changes['initialAnswers'] || changes['initialIndex']) {
      this.hydrate();
    }
  }

  get counterLabel(): string {
    return (this.content?.questionCounterTemplate ?? '')
      .replace('{current}', String(this.current.number))
      .replace('{total}', String(this.questions.length));
  }

  private buildSteps(): Step[] {
    return [...(this.content?.steps ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((step, i) => ({
        icon: SALES_PROFILE_STEP_ICONS[i],
        title: step.title,
        sub: step.sub,
      }));
  }

  /**
   * Merges the fixed structure with the stored wording. Joined on question
   * number and option key rather than position, so a reordered payload can't
   * silently attach a label to the wrong tag.
   */
  private buildQuestions(): Question[] {
    if (!this.content) {
      return [];
    }
    return SALES_PROFILE_QUESTIONS.map((structure) => {
      const text = this.content!.questions.find(
        (q) => q.number === structure.number,
      );
      return {
        number: structure.number,
        min: structure.min,
        max: structure.max,
        hasFreeText: structure.hasFreeText,
        title: text?.title ?? '',
        hint: text?.hint ?? '',
        note: text?.note ?? undefined,
        freeTextLabel: text?.freeTextLabel ?? undefined,
        freeTextPlaceholder: text?.freeTextPlaceholder ?? undefined,
        options: structure.options.map((option) => ({
          id: option.id,
          tag: option.tag,
          label:
            text?.options.find((o) => o.optionKey === option.id)?.label ?? '',
        })),
      };
    });
  }

  /** Restore selections / freeText / currentIndex from saved progress once. */
  private hydrate(): void {
    if (this.hydrated) {
      return;
    }
    if (this.initialAnswers) {
      try {
        const parsed = JSON.parse(this.initialAnswers) as {
          answers?: { q?: number; options?: string[] }[];
          freeText?: string;
        };
        const saved = Array.isArray(parsed?.answers) ? parsed.answers : [];
        SALES_PROFILE_QUESTIONS.forEach((question, i) => {
          const match = saved.find((a) => a?.q === question.number);
          const options = Array.isArray(match?.options) ? match.options : [];
          const validIds = new Set(question.options.map((o) => o.id));
          this.selections[i] = options
            .filter((id) => validIds.has(id))
            .slice(0, question.max);
        });
        if (typeof parsed?.freeText === 'string') {
          this.freeText = parsed.freeText;
        }
      } catch {
        // Malformed saved data -> ignore and keep the empty questionnaire.
      }
    }
    const last = SALES_PROFILE_QUESTIONS.length - 1;
    this.currentIndex = Math.min(Math.max(0, this.initialIndex || 0), last);
    this.hydrated = true;
  }

  get current(): Question {
    return this.questions[this.currentIndex];
  }

  get currentSelection(): string[] {
    return this.selections[this.currentIndex];
  }

  get isFirst(): boolean {
    return this.currentIndex === 0;
  }

  get isLast(): boolean {
    return this.currentIndex === this.questions.length - 1;
  }

  isSelected(optionId: string): boolean {
    return this.currentSelection.includes(optionId);
  }

  toggle(optionId: string): void {
    const selected = this.currentSelection;
    const idx = selected.indexOf(optionId);
    if (idx > -1) {
      selected.splice(idx, 1);
    } else if (selected.length < this.current.max) {
      selected.push(optionId);
    }
  }

  get canProceed(): boolean {
    return this.currentSelection.length >= this.current.min;
  }

  next(): void {
    if (!this.canProceed) {
      return;
    }
    if (this.isLast) {
      // Completion path — kept exactly as-is (host saves with isCompleted:true).
      this.finish.emit({
        answersJson: JSON.stringify(this.buildAnswers()),
        freeText: this.freeText.trim(),
      });
      return;
    }
    this.currentIndex++;
    this.emitProgress();
  }

  private emitProgress(): void {
    this.progress.emit({
      answersJson: JSON.stringify(this.buildAnswers()),
      freeText: this.freeText.trim(),
      currentIndex: this.currentIndex,
    });
  }

  private buildAnswers() {
    return {
      answers: this.questions.map((q, i) => ({
        q: q.number,
        options: this.selections[i],
        tags: this.selections[i].map(
          (id) => q.options.find((o) => o.id === id)?.tag,
        ),
      })),
    };
  }

  back(): void {
    if (!this.isFirst) {
      this.currentIndex--;
      this.emitProgress();
    }
  }

  goTo(index: number): void {
    if (index >= 0 && index < this.questions.length) {
      this.currentIndex = index;
      this.emitProgress();
    }
  }
}
